import { useEffect, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Heading } from '@/components/ui/heading';
import type { ChatMessage } from '@domain/chat';
import { useChatStream } from '@/hooks/useChatStream';
import { ChatInterface } from '@/components/chat/ChatInterface';
import { mapStreamToChatMessages } from '../flowUtils';
import { JOURNAL_STEP_ICONS } from './stepIcons';

export type CoachPanelProps = {
  entryId: number;
  initialMessages: ChatMessage[];
  onTranscriptChange: (messages: ChatMessage[]) => void;
  onGenerateDraft: () => void;
  generatingDraft: boolean;
  errorMessage?: string | null;
};

export default function CoachPanel({
  entryId,
  initialMessages,
  onTranscriptChange,
  onGenerateDraft,
  generatingDraft,
  errorMessage,
}: CoachPanelProps) {
  const StepIcon = JOURNAL_STEP_ICONS.coach;
  const {
    input,
    setInput,
    msgList,
    loading,
    error,
    streamingMsg,
    bottomRef,
    inputRef,
    handleSend,
    copiedId,
    handleCopy,
    image,
    setImage,
  } = useChatStream({
    streamUrl: `/patients/journals/${entryId}/coach/messages`,
    initialMessages,
    bodyFactory: (content) => ({ message: content }),
    autoStart: true,
  });

  useEffect(() => {
    onTranscriptChange(mapStreamToChatMessages(msgList));
  }, [msgList, onTranscriptChange]);

  const disableDraftGeneration = generatingDraft || loading || Boolean(streamingMsg);

  const handleGenerateDraft = useCallback(() => {
    onGenerateDraft();
  }, [onGenerateDraft]);

  const generateDraftControls = useMemo(() => {
    if (msgList.length < 4) {
      return null;
    } else {
      return (
        <Card className="flex flex-row flex-wrap p-2 items-center justify-between gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={handleGenerateDraft}
            disabled={disableDraftGeneration}
          >
            {generatingDraft ? 'Sto preparando la bozza...' : 'Genera bozza con il coach'}
          </Button>
          {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
          {!errorMessage && (
            <p className="text-sm text-muted-foreground">
              Quando senti di essere pronto, chiedimi di generare la tua pagina di diario!
            </p>
          )}
        </Card>
      );
    }
  }, [msgList, errorMessage, generatingDraft, disableDraftGeneration, handleGenerateDraft]);

  return (
    <Card className="border-0 bg-transparent h-full">
      <div className="flex items-center justify-between">
        <Heading level={2} icon={StepIcon}>
          Sessione con il coach
        </Heading>
      </div>

      <ChatInterface
        assistantLabel="Coach"
        input={input}
        setInput={setInput}
        msgList={msgList}
        loading={loading}
        error={error}
        streamingMsg={streamingMsg}
        bottomRef={bottomRef}
        inputRef={inputRef}
        copiedId={copiedId}
        handleCopy={handleCopy}
        handleSend={handleSend}
        image={image}
        setImage={setImage}
        allowAttachments={false}
        heightClass="h-full"
        bottomSection={generateDraftControls}
      />
    </Card>
  );
}
